import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { QueryKey } from 'constants/query-key';
import { type Subscriptions } from 'interfaces/subscription';
import type { ProductData } from 'services/api/accounts/interfaces';
import { ApiManager } from 'services/api/api-manager';
import type { ISubscriptionDataV3 } from 'services/api/billing-v3/interfaces';
import { getOrganizationId } from 'store/entities/accounts/selectors';

const fetchMyProducts = async (): Promise<ProductData[]> => {
  const { result, error } = await ApiManager.accountsApi.getMyProducts();

  if (error) {
    throw error;
  }

  return result;
};

const fetchSubscriptions = async (): Promise<ISubscriptionDataV3> => {
  const { result, error } = await ApiManager.billingApiV3.fetchActivatedSubscriptions();

  if (error) {
    throw error;
  }

  return result;
};

interface UseProductData {
  organizationId: string;
  installedProducts: ProductData[];
  subscriptions: Subscriptions;
  areProductsFetched: boolean;
}

export const useProductData = (): UseProductData => {
  const organizationId = useSelector(getOrganizationId);
  const { data: installedProducts, isFetched: areProductsFetched } = useQuery([QueryKey.MyProducts], fetchMyProducts, {
    refetchOnWindowFocus: false,
    initialData: [],
  });
  const {
    data: { subscriptions },
  } = useQuery([QueryKey.ActivatedSubscriptions], fetchSubscriptions, {
    refetchOnWindowFocus: false,
    initialData: () => ({ subscriptions: {} }),
  });

  return {
    organizationId,
    installedProducts,
    subscriptions,
    areProductsFetched,
  };
};
